import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { User, ChevronLeft, Car, Wrench, Hammer, Package, Shield, Calendar, MessageSquare, Target, AlertTriangle, Star, Users, Plus, Trash2 } from "lucide-react";
import { api, formatError } from "../lib/api.js";
import { fmtDate } from "../lib/format.js";

const TABS = [
  { key: "summary", label: "Resumo", icon: User },
  { key: "vehicles", label: "Viaturas", icon: Car },
  { key: "fleet_contacts", label: "Contactos frota", icon: Users, fleet_only: true },
  { key: "opportunities", label: "Oportunidades", icon: Target },
  { key: "interactions", label: "Interacções", icon: MessageSquare },
  { key: "service_cases", label: "SOMs", icon: Wrench },
  { key: "body_shop_cases", label: "BPS", icon: Hammer },
  { key: "parts_bench_orders", label: "PEB", icon: Package },
  { key: "warranty_claims", label: "Garantias", icon: Shield },
  { key: "maintenance_plans", label: "MPM", icon: Calendar },
  { key: "complaints", label: "Reclamações", icon: AlertTriangle },
  { key: "surveys", label: "Satisfação", icon: Star },
];

export default function CustomerProfilePage() {
  const { id } = useParams();
  const [profile, setProfile] = useState(null);
  const [tab, setTab] = useState("summary");

  const load = async () => {
    const { data } = await api.get(`/crm/customers/${id}/profile`);
    setProfile(data);
  };
  useEffect(() => { load(); }, [id]);

  if (!profile) return <div className="p-8 text-sm text-[#6C757D]">A carregar perfil…</div>;
  const s = profile.stats || {};

  return (
    <div className="max-w-[1600px] mx-auto">
      <div className="sticky top-0 z-30 bg-white border-b border-gray-200 px-4 sm:px-6 py-3 shadow-sm">
        <div className="flex items-center gap-2 text-xs text-[#6C757D] mb-1">
          <Link to="/customers" className="flex items-center gap-1 hover:text-[#1F3864]"><ChevronLeft className="w-3 h-3" /> Clientes</Link>
          <span>/</span><span className="font-mono">{profile.customer_number}</span>
        </div>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-heading text-xl font-bold flex items-center gap-2" data-testid="customer-profile-name"><User className="w-5 h-5 text-[#1F3864]" />{profile.name}</h1>
            <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#6C757D]">
              <span>{profile.customer_number}</span>
              <span>Tipo: <b className="text-[#1A1A2E]">{profile.customer_type || "Individual"}</b></span>
              {profile.is_fleet && <span className="text-[#1F3864] font-semibold">· Frota</span>}
              <span>{profile.phone}</span>
              {profile.email && <span>{profile.email}</span>}
              {s.needs_outreach && <span className="text-[#DC3545] font-semibold">· Sem contacto há {s.days_since_last_contact}d</span>}
            </div>
          </div>
          <div className="flex gap-3 text-xs">
            <Stat label="Viaturas" value={s.vehicle_count || 0} />
            <Stat label="Opps abertas" value={s.open_opportunities || 0} />
            <Stat label="Reclamações" value={s.open_complaints || 0} accent={s.open_complaints ? "red" : ""} />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-4 sm:px-6 pt-3 flex gap-1 flex-wrap border-b border-gray-200 bg-white" data-testid="customer-profile-tabs">
        {TABS.filter((t) => !t.fleet_only || profile.is_fleet).map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-3 py-2 text-xs font-semibold border-b-2 -mb-px transition ${tab === t.key ? "border-[#1F3864] text-[#1F3864]" : "border-transparent text-[#6C757D] hover:text-[#1A1A2E]"}`}
            data-testid={`customer-tab-${t.key}`}
          >
            <span className="inline-flex items-center gap-1"><t.icon className="w-3 h-3" />{t.label}</span>
            {Array.isArray(profile[t.key]) && profile[t.key].length > 0 && <span className="ml-1 text-[10px] text-[#6C757D]">({profile[t.key].length})</span>}
          </button>
        ))}
      </div>

      <div className="p-4 sm:p-6 space-y-4">
        {tab === "summary" && <SummaryTab p={profile} />}
        {tab === "vehicles" && <SimpleList items={profile.vehicles} fields={["registration_plate", "brand", "model", "sale_date"]} testid="vehicles" />}
        {tab === "fleet_contacts" && <FleetContactsTab customerId={profile.id} onReload={load} />}
        {tab === "opportunities" && <SimpleList items={profile.opportunities} fields={["number", "current_state", "brand", "expected_amount", "lead_tier", "lead_score"]} testid="opps" />}
        {tab === "interactions" && <SimpleList items={profile.interactions} fields={["occurred_at", "interaction_type", "subject", "created_by_name"]} testid="interactions" />}
        {tab === "service_cases" && <SimpleList items={profile.service_cases} fields={["number", "vehicle_registration", "current_status", "opened_at"]} testid="soms" link={(r) => `/service-cases/${r.id}`} />}
        {tab === "body_shop_cases" && <SimpleList items={profile.body_shop_cases} fields={["number", "vehicle_registration", "admin_state", "workshop_state"]} testid="bps" link={(r) => `/body-shop-cases/${r.id}`} />}
        {tab === "parts_bench_orders" && <SimpleList items={profile.parts_bench_orders} fields={["number", "current_status", "erp_reference", "created_at"]} testid="pebs" link={(r) => `/parts-bench-orders/${r.id}`} />}
        {tab === "warranty_claims" && <SimpleList items={profile.warranty_claims} fields={["number", "status", "created_at"]} testid="warranties" />}
        {tab === "maintenance_plans" && <SimpleList items={profile.maintenance_plans} fields={["number", "plan_name", "status", "valid_to"]} testid="mpms" />}
        {tab === "complaints" && <SimpleList items={profile.complaints} fields={["number", "category", "subject", "current_state", "opened_at"]} testid="complaints" />}
        {tab === "surveys" && <SimpleList items={profile.surveys} fields={["number", "survey_type", "department", "status", "score_average", "nps_classification"]} testid="surveys" />}
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  const color = accent === "red" ? "text-[#DC3545]" : "text-[#1A1A2E]";
  return (
    <div className="text-right">
      <div className="text-[10px] uppercase tracking-widest text-[#6C757D]">{label}</div>
      <div className={`font-heading text-2xl font-bold ${color}`}>{value}</div>
    </div>
  );
}

function SummaryTab({ p }) {
  const s = p.stats || {};
  return (
    <div className="bg-white border border-gray-200 rounded-md p-4" data-testid="customer-tab-summary">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Viaturas" value={s.vehicle_count || 0} />
        <StatCard label="SOMs" value={s.som_count || 0} />
        <StatCard label="BPS" value={s.bps_count || 0} />
        <StatCard label="PEB" value={s.peb_count || 0} />
      </div>
      {p.notes && <div className="mt-4"><div className="text-[10px] uppercase tracking-widest text-[#6C757D] mb-1">Notas</div><div className="text-sm">{p.notes}</div></div>}
    </div>
  );
}
function StatCard({ label, value }) {
  return (
    <div className="bg-[#FAFBFC] border border-gray-200 rounded-sm p-3 text-center">
      <div className="text-[10px] uppercase tracking-widest text-[#6C757D]">{label}</div>
      <div className="font-heading text-2xl font-bold text-[#1F3864]">{value}</div>
    </div>
  );
}

function SimpleList({ items, fields, testid, link }) {
  if (!items || items.length === 0) {
    return <div className="bg-white border border-gray-200 rounded-md p-6 text-center text-sm text-[#6C757D] italic" data-testid={`${testid}-empty`}>Sem registos.</div>;
  }
  return (
    <div className="bg-white border border-gray-200 rounded-md overflow-x-auto" data-testid={`${testid}-list`}>
      <table className="w-full text-sm">
        <thead className="bg-[#F4F6F8] border-b border-gray-200">
          <tr>{fields.map((f) => <th key={f} className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-[#6C757D]">{f.replace(/_/g, " ")}</th>)}</tr>
        </thead>
        <tbody>
          {items.map((it, idx) => (
            <tr key={it.id || idx} className="border-b border-gray-100 hover:bg-[#FAFBFC]">
              {fields.map((f) => {
                const v = it[f];
                const isDate = f.includes("date") || f.includes("_at");
                const rendered = isDate && v ? fmtDate(v) : (v == null ? "—" : String(v));
                if (f === fields[0] && link) {
                  return <td key={f} className="px-3 py-2 font-mono text-[#1F3864]"><Link to={link(it)} className="hover:underline">{rendered}</Link></td>;
                }
                return <td key={f} className="px-3 py-2 text-xs">{rendered}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FleetContactsTab({ customerId, onReload }) {
  const [contacts, setContacts] = useState([]);
  const [roles, setRoles] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    const [{ data: cs }, { data: r }] = await Promise.all([
      api.get(`/crm/customers/${customerId}/contacts`),
      api.get("/crm/meta/fleet-roles"),
    ]);
    setContacts(cs); setRoles(r.roles);
  };
  useEffect(() => { load(); }, [customerId]);

  const removeContact = async (id) => {
    if (!window.confirm("Remover este contacto?")) return;
    try { await api.delete(`/crm/fleet-contacts/${id}`); load(); } catch (e) { setErr(formatError(e.response?.data?.detail)); }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-md" data-testid="fleet-contacts-tab">
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <div className="font-heading font-semibold text-sm text-[#1A1A2E]">Contactos da frota</div>
        <button onClick={() => setShowNew(true)} className="text-xs bg-[#1F3864] hover:bg-[#142542] text-white rounded-sm px-3 py-1.5 flex items-center gap-1" data-testid="fleet-contact-add-btn">
          <Plus className="w-3 h-3" /> Adicionar
        </button>
      </div>
      {err && <div className="px-4 py-2 bg-[#F8D7DA] text-[#721C24] text-sm">{err}</div>}
      {contacts.length === 0 ? (
        <div className="text-center text-sm text-[#6C757D] italic py-6">Nenhum contacto registado.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-[#F4F6F8] border-b border-gray-200">
            <tr>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-[#6C757D]">Nome</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-[#6C757D]">Função</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-[#6C757D]">Telefone</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-[#6C757D]">Email</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-[#6C757D]">Principal</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {contacts.map((c) => (
              <tr key={c.id} className="border-b border-gray-100 hover:bg-[#FAFBFC]" data-testid={`fleet-contact-row-${c.id}`}>
                <td className="px-3 py-2 font-semibold">{c.name}</td>
                <td className="px-3 py-2 text-xs">{c.role}</td>
                <td className="px-3 py-2 font-mono text-xs">{c.phone || "—"}</td>
                <td className="px-3 py-2 text-xs">{c.email || "—"}</td>
                <td className="px-3 py-2 text-xs">{c.is_primary ? "✓" : ""}</td>
                <td className="px-3 py-2"><button onClick={() => removeContact(c.id)} className="text-[#DC3545] hover:text-[#A51C2A]"><Trash2 className="w-3.5 h-3.5" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {showNew && <NewContactDialog customerId={customerId} roles={roles} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); onReload && onReload(); }} />}
    </div>
  );
}

function NewContactDialog({ customerId, roles, onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", role: roles[0] || "Compras", phone: "", email: "", is_primary: false });
  const [err, setErr] = useState("");
  const submit = async () => {
    try { await api.post(`/crm/customers/${customerId}/contacts`, form); onSaved(); }
    catch (e) { setErr(formatError(e.response?.data?.detail)); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="fleet-contact-dialog">
      <div className="bg-white rounded-md border border-gray-300 w-full max-w-md">
        <div className="px-4 py-3 border-b border-gray-200"><h3 className="font-heading font-bold">Novo contacto de frota</h3></div>
        <div className="p-4 space-y-3">
          {err && <div className="px-3 py-2 bg-[#F8D7DA] text-[#721C24] border border-[#DC3545]/30 rounded-sm text-sm">{err}</div>}
          <label className="block"><span className="text-[10px] uppercase tracking-widest text-[#6C757D]">Nome *</span><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-gray-300 rounded-sm px-2 py-1.5 text-sm" data-testid="fleet-contact-name" /></label>
          <label className="block"><span className="text-[10px] uppercase tracking-widest text-[#6C757D]">Função</span>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full border border-gray-300 rounded-sm px-2 py-1.5 text-sm">{roles.map((r) => <option key={r}>{r}</option>)}</select>
          </label>
          <label className="block"><span className="text-[10px] uppercase tracking-widest text-[#6C757D]">Telefone</span><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full border border-gray-300 rounded-sm px-2 py-1.5 text-sm font-mono" /></label>
          <label className="block"><span className="text-[10px] uppercase tracking-widest text-[#6C757D]">Email</span><input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-gray-300 rounded-sm px-2 py-1.5 text-sm" /></label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_primary} onChange={(e) => setForm({ ...form, is_primary: e.target.checked })} data-testid="fleet-contact-primary" /> Contacto principal</label>
        </div>
        <div className="px-4 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onClose} className="text-sm px-3 py-1.5 border border-gray-300 rounded-sm">Cancelar</button>
          <button onClick={submit} disabled={!form.name.trim()} className="text-sm bg-[#1F3864] hover:bg-[#142542] text-white px-4 py-1.5 rounded-sm disabled:opacity-50" data-testid="fleet-contact-save-btn">Guardar</button>
        </div>
      </div>
    </div>
  );
}

